<template>
  <div class="ui-button-select" :class="classList" v-click-outside="outsideClickEvent">
    <ui-button
      v-if="!loading"
      v-bind="$attrs"
      :type="type"
      :button-style="buttonStyle"
      :icon="iconName"
      :icon-size="iconSize"
      class="ui-button-select__button"
      ref="button"
      @click="toggle"
    >
      <div v-if="hasContent" class="ui-button-select__button-content">
        <ui-icon
          v-if="iconName"
          class="ui-button-select__icon"
          :name="iconName"
          :size="iconSize"
        />
        <slot>
          <div class="ui-button-select__title">
            {{ title }}
          </div>
        </slot>
        <ui-icon
          v-if="isShowArrow"
          class="ui-button-select__arrow"
          name="bottom"
        />
      </div>
    </ui-button>
    <ui-skeleton
      v-else
      class="ui-button-select__skeleton"
    />
    <ui-dropdown
      class="ui-button-select__dropdown"
      v-if="isOpen"
      :container="$el"
      :min-width="minWidth"
      :position-x="dropdownPositionX"
      :position-y="dropdownPositionY"
    >
      <div class="ui-button-select__list">
        <div
          v-for="item in list"
          :key="item.id"
          class="ui-button-select__list--item"
          @click.stop="change(item)"
        >
          {{ item[titleFieldName] }}
        </div>
      </div>
    </ui-dropdown>
  </div>
</template>

<script>
import UiButton from './button/UiButton.vue'
import UiIcon from './icons/UiIcon.vue'
import UiSkeleton from './skeleton/UiSkeleton.vue'
import UiDropdown from './UiDropdown.vue'

export default {
  components: {
    UiButton,
    UiIcon,
    UiSkeleton,
    UiDropdown
  },
  props: {
    list: {
      type: Array,
      default: () => []
    },
    title: {
      type: String,
      default: ''
    },
    titleFieldName: {
      type: String,
      default: 'name'
    },
    type: {
      type: String,
      default: 'secondary'
    },
    buttonStyle: {
      type: String,
      default: 'outline'
    },
    maxHeight: {
      type: Number,
      default: 200
    },
    maxWidth: {
      type: Number,
      default: 300
    },
    minWidth: {
      type: Number,
      default: 250
    },
    iconName: String,
    iconSize: {
      type: Number,
      default: 24
    },
    loading: {
      type: Boolean,
      default: false
    },
    hasArrow: {
      type: Boolean,
      default: true
    },
    border: {
      type: Boolean,
      default: true
    },
    dropdownPositionX: String,
    dropdownPositionY: String
  },
  data () {
    return {
      isOpen: false
    }
  },
  computed: {
    hasContent () {
      return Boolean(this.title || this.$slots.default)
    },
    outsideClickEvent () {
      return {
        handler: this.close,
        events: ['click'],
        isActive: this.isOpen
      }
    },
    classList () {
      return {
        active: this.isOpen
      }
    },
    isShowArrow () {
      return this.hasArrow && this.hasContent
    }
  },
  methods: {
    change (item) {
      item.event?.()
      this.$emit('change', item.id)
      this.close()
    },
    toggle () {
      if (this.isOpen) this.close()
      else this.open()
    },
    close () {
      this.isOpen = false
      this.$emit('close')
    },
    open () {
      this.isOpen = true
      this.$emit('open')
    }
  }
}
</script>

<style lang="scss" scoped>
.ui-button-select {
  $parent: &;
  position: relative;
  display: inline-flex;
  flex-direction: column;

  &.active {
    #{$parent}__arrow {
      transform: rotate(180deg);
    }
  }

  &__button-content {
    @include flex-center;
    width: 100%;

    @include nextChildren {
      margin-left: 10px;
    }
  }

  &__title {
    text-align: left;
    flex-grow: 1;
  }

  &__button {
    align-self: unset;

    .ui-button__title {
      display: inline-flex;
      align-items: center;
    }
  }

  &__icon {
    fill: $primary;
  }

  &__skeleton {
    @include skeleton(40px, $border-radius-01);
  }

  &__arrow {
    @include transition-list(transform);
    margin-left: 9px;
  }

  &__dropdown {
    min-width: 100%;
  }

  &__list {
    display: flex;
    flex-direction: column;
    width: 100%;
    background: $white;

    &--item {
      @include font-16;
      display: flex;
      align-items: center;
      min-height: 44px;
      padding: 10px 12px;
      word-break: break-word;
      border-radius: $border-radius-01;
      cursor: pointer;

      &:hover {
        color: $primary;
        background: $gray-6;
      }
    }
  }

  @include media-max-sm {
    &__select {
      min-width: 200px;
    }
  }
}
</style>
